import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import DarkNav from '../../components/dark-nav'
import { getCanonicalUrl } from '../../utils/canonical'
import './shopify.css'

const ShopifyIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({ duration: 800, easing: 'ease-out', once: false, offset: 100, mirror: true })
    }
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') AOS.refresh()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const trackEvent = (name, props = {}) => {
    if (typeof gtag !== 'undefined') gtag('event', name, props)
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Shopify' }
  ]

  const notifications = [
    { icon: '🛒', title: 'Order Confirmation', desc: 'SMS sent the moment a customer places an order.' },
    { icon: '📦', title: 'Shipping Confirmation', desc: 'SMS sent when the order is fulfilled and shipped.' },
    { icon: '🚚', title: 'Out for Delivery', desc: 'SMS sent when the carrier marks the order out for delivery.' },
    { icon: '✅', title: 'Delivered', desc: 'SMS sent when the order is marked as delivered.' },
    { icon: '❌', title: 'Order Cancelled', desc: 'SMS sent when an order is cancelled.' },
    { icon: '💰', title: 'Refund Confirmation', desc: 'SMS sent when a refund is processed.' },
    { icon: '👋', title: 'Welcome SMS', desc: 'SMS sent when a new customer account is created in Shopify.' },
    { icon: '🛍️', title: 'Abandoned Cart Recovery', desc: 'SMS sent to customers who left items in their cart, with a configurable delay from 5 minutes to 24 hours.' },
  ]

  const features = [
    { icon: '💬', title: 'Two-Way Inbox', desc: 'iMessage-style conversation threads grouped by customer. Reply directly from Shopify Admin.' },
    { icon: '📢', title: 'Bulk Campaigns', desc: 'Broadcast to all opted-in subscribers in one click with AI-generated copy variants.' },
    { icon: '🤖', title: 'AI Copy Generator', desc: 'Describe your promotion and get 3 ready-to-send SMS variants generated by AI.' },
    { icon: '🎯', title: '9 Audience Segments', desc: 'Target All Subscribers, VIP, Lapsed, At-Risk, Abandoned Cart, Loyalty Members, and more.' },
    { icon: '📊', title: 'Analytics', desc: 'Track SMS sent, delivered, and failed with delivery rate breakdowns per notification type.' },
    { icon: '⚡', title: 'Shopify Flow', desc: 'Use DataFlows SMS inside any Shopify Flow automation — trigger SMS from any Flow event.' },
  ]

  const faqItems = [
    {
      q: 'Does DataFlows SMS work with Shopify?',
      a: 'Yes. DataFlows SMS is available on the Shopify App Store at apps.shopify.com/dataflows-sms. Install it from the Shopify App Store, click Connect DataFlows, then sign in to DataFlows and approve access. There is no API key to copy.'
    },
    {
      q: 'Can I send automated order notifications via SMS on Shopify?',
      a: 'Yes. The app supports 8 automated notification types including Order Confirmation, Shipping, Out for Delivery, Delivered, Cancelled, Refund, Welcome SMS, and Abandoned Cart Recovery. All are individually togglable and send automatically via Shopify webhooks.'
    },
    {
      q: 'Does the Shopify SMS app support two-way messaging?',
      a: 'Yes. The inbox feature shows iMessage-style conversation threads grouped by customer phone number. You can view inbound messages and reply directly from inside Shopify Admin.'
    },
    {
      q: 'Is SMS marketing via Shopify compliant with Australian law?',
      a: 'Yes. DataFlows is an ACMA-registered Originating Telco and fully compliant with the Spam Act 2003. STOP keyword is always active and cannot be disabled. Opt-outs are tracked per subscriber and honoured across all automations.'
    },
    {
      q: 'Can I use DataFlows SMS with Shopify Flow?',
      a: 'Yes. The app includes a Send SMS action and an Add to SMS List action for Shopify Flow, allowing you to fire SMS from any Flow trigger such as a new order, loyalty point earned, or tag added.'
    },
    {
      q: 'What variables are available in SMS message templates?',
      a: 'Templates support {{customer.first_name}}, {{order.name}}, {{shop.name}}, {{tracking_url}}, and {{checkout_url}}. Each of the 8 notification types has its own fully customisable message template.'
    },
  ]

  return (
    <div className="shopify-container">
      <Helmet>
        <title>Shopify SMS App — Automated Order Notifications & Marketing | DataFlows</title>
        <meta name="description" content="Send automated SMS order notifications, abandoned cart recovery, and bulk campaigns from Shopify. ACMA-compliant Australian SMS. Available on the Shopify App Store." />
        <meta name="keywords" content="Shopify SMS app, Shopify SMS notifications, Shopify order SMS, Shopify abandoned cart SMS, Shopify SMS marketing, Shopify SMS Australia, DataFlows Shopify, Shopify bulk SMS, Shopify Flow SMS, WooCommerce alternative SMS, Shopify SMS automation" />
        <meta property="og:title" content="Shopify SMS App — Order Notifications & Marketing | DataFlows" />
        <meta property="og:description" content="Automated SMS order notifications, two-way inbox, and bulk campaigns for Shopify. ACMA-compliant Australian SMS platform." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={getCanonicalUrl('/integrations/shopify')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "DataFlows SMS — Shopify App",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Shopify",
          "url": "https://apps.shopify.com/dataflows-sms",
          "description": "Automated SMS order notifications, two-way inbox, bulk campaigns, and Shopify Flow integration for Australian businesses.",
          "offers": { "@type": "Offer", "price": "35.00", "priceCurrency": "AUD" },
          "author": { "@type": "Organization", "name": "DataFlows", "url": "https://dataflows.com.au" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "Shopify", "item": "https://dataflows.com.au/integrations/shopify" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      <DarkNav />

      {/* Breadcrumbs */}
      <div className="shopify-breadcrumbs">
        <div className="shopify-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero */}
      <section className="shopify-hero" data-aos="fade-up">
        <div className="shopify-inner">
          <div className="shopify-hero-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/></svg>
            Shopify App Store
          </div>
          <h1 className="shopify-hero-title">DataFlows SMS for Shopify</h1>
          <p className="shopify-hero-subtitle">
            Automated order notifications, abandoned cart recovery, two-way inbox, and bulk SMS campaigns — all inside Shopify Admin. Built for Australian businesses, ACMA compliant.
          </p>
          <div className="shopify-hero-actions">
            <a
              href="https://apps.shopify.com/dataflows-sms"
              target="_blank"
              rel="noreferrer noopener"
              className="shopify-btn-primary"
              onClick={() => trackEvent('shopify_app_store_clicked', { source: 'hero' })}
            >
              Install on Shopify
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </a>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="shopify-btn-outline">
              Create a DataFlows Account
            </a>
          </div>
          <div className="shopify-hero-trust">
            <span>✓ ACMA Registered Originating Telco</span>
            <span>✓ Spam Act 2003 Compliant</span>
            <span>✓ No lock-in contracts</span>
          </div>
        </div>
      </section>

      {/* Order Notifications */}
      <section className="shopify-section" id="notifications" data-aos="fade-up">
        <div className="shopify-inner">
          <div className="shopify-section-header">
            <span className="shopify-label">Automated</span>
            <h2>8 Order Notification Types</h2>
            <p>All notifications send automatically via Shopify webhooks — no manual action needed. Toggle each one on or off individually from your Shopify Admin.</p>
          </div>
          <div className="shopify-notifications-grid">
            {notifications.map((n, i) => (
              <div key={i} className="shopify-notification-card" data-aos="fade-up" data-aos-delay={i * 50}>
                <span className="shopify-notification-icon">{n.icon}</span>
                <h3>{n.title}</h3>
                <p>{n.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Message Templates */}
      <section className="shopify-section shopify-section--dark" id="templates" data-aos="fade-up">
        <div className="shopify-inner">
          <div className="shopify-section-header">
            <span className="shopify-label">Customisable</span>
            <h2>Message Templates</h2>
            <p>Every notification type has its own fully customisable SMS template. Use personalisation variables to make every message feel personal.</p>
          </div>
          <div className="shopify-template-box">
            <div className="shopify-template-preview">
              <div className="shopify-template-bubble">
                Hi {'{{customer.first_name}}'}, your {'{{shop.name}}'} order {'{{order.name}}'} has been shipped! Track it here: {'{{tracking_url}}'}
              </div>
              <span className="shopify-template-label">Shipping Confirmation example</span>
            </div>
            <div className="shopify-variables">
              <h3>Available Variables</h3>
              <div className="shopify-vars-grid">
                {['{{customer.first_name}}', '{{order.name}}', '{{shop.name}}', '{{tracking_url}}', '{{checkout_url}}'].map(v => (
                  <code key={v}>{v}</code>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="shopify-section" id="features" data-aos="fade-up">
        <div className="shopify-inner">
          <div className="shopify-section-header">
            <span className="shopify-label">Platform</span>
            <h2>Everything You Need to SMS at Scale</h2>
          </div>
          <div className="shopify-features-grid">
            {features.map((f, i) => (
              <div key={i} className="shopify-feature-card" data-aos="fade-up" data-aos-delay={i * 60}>
                <span className="shopify-feature-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two-Way Inbox */}
      <section className="shopify-section shopify-section--dark" id="inbox" data-aos="fade-up">
        <div className="shopify-inner">
          <div className="shopify-two-col">
            <div className="shopify-two-col-text">
              <span className="shopify-label">Inbox</span>
              <h2>Two-Way SMS Conversations</h2>
              <p>View all inbound messages from customers and reply directly from inside Shopify Admin. Conversations are grouped by customer phone number in an iMessage-style thread view.</p>
              <ul className="shopify-checklist">
                <li>Search conversations by phone or message content</li>
                <li>Auto-refreshes every 15 seconds with live indicator</li>
                <li>Message delivery status — Sending to Delivered</li>
              </ul>
            </div>
            <div className="shopify-inbox-preview">
              <div className="shopify-inbox-thread">
                <div className="shopify-msg shopify-msg--in">Hi, when will my order arrive?</div>
                <div className="shopify-msg shopify-msg--out">Hi Sarah! Your order #1042 is out for delivery today. Track it: dataflows.com.au/track/1042</div>
                <div className="shopify-msg shopify-msg--in">Thanks! 😊</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscriber Management */}
      <section className="shopify-section" id="subscribers" data-aos="fade-up">
        <div className="shopify-inner">
          <div className="shopify-section-header">
            <span className="shopify-label">Compliance</span>
            <h2>Subscriber Management</h2>
            <p>Sync customers from Shopify, manage opt-in status, and stay compliant with Australian SMS law.</p>
          </div>
          <div className="shopify-compliance-grid">
            <div className="shopify-compliance-card">
              <h3>Subscriber Sync</h3>
              <p>Pull up to 250 Shopify customers with phone numbers in one click. View name, phone, email, opt-in status, and opt-in date for every subscriber.</p>
            </div>
            <div className="shopify-compliance-card">
              <h3>Keywords</h3>
              <p>Configure opt-in keyword (e.g. JOIN, VIP20), opt-in confirmation reply, opt-out keyword (beyond STOP), and HELP response message from Settings.</p>
            </div>
            <div className="shopify-compliance-card shopify-compliance-card--highlight">
              <h3>Always Compliant</h3>
              <p>STOP is always active and cannot be disabled — required by the Australian Spam Act, enforced by ACMA. Opt-outs are tracked per subscriber and honoured across all automations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shopify Flow */}
      <section className="shopify-section shopify-section--dark" id="flow" data-aos="fade-up">
        <div className="shopify-inner">
          <div className="shopify-section-header">
            <span className="shopify-label">Shopify Flow</span>
            <h2>Use SMS in Any Shopify Automation</h2>
            <p>DataFlows SMS works inside Shopify Flow, giving you two actions you can drop into any workflow.</p>
          </div>
          <div className="shopify-flow-grid">
            <div className="shopify-flow-card">
              <div className="shopify-flow-card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              </div>
              <h3>Send SMS Action</h3>
              <p>Fire an SMS from any Flow trigger — new order, loyalty point earned, tag added, review submitted, and more.</p>
            </div>
            <div className="shopify-flow-card">
              <div className="shopify-flow-card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87m-4-12a4 4 0 010 7.75"/></svg>
              </div>
              <h3>Add to SMS List Action</h3>
              <p>Add a customer to your DataFlows contact group directly from a Shopify Flow step — perfect for loyalty and VIP programmes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="shopify-section" id="faq" data-aos="fade-up">
        <div className="shopify-inner">
          <div className="shopify-section-header">
            <span className="shopify-label">FAQ</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="shopify-faq">
            {faqItems.map((item, i) => (
              <div key={i} className={`shopify-faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="shopify-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <p className={`shopify-faq-a${openFaq === i ? '' : ' shopify-faq-a--hidden'}`}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="shopify-section shopify-section--dark" data-aos="fade-up">
        <div className="shopify-inner">
          <h2 className="shopify-related-title">Related Integrations</h2>
          <div className="shopify-related-links">
            <Link to="/integrations/zapier" className="shopify-related-link">Zapier SMS</Link>
            <Link to="/integrations/power-automate" className="shopify-related-link">Power Automate SMS</Link>
            <Link to="/integrations/wordpress" className="shopify-related-link">WordPress / WooCommerce SMS</Link>
            <Link to="/integrations/auth0" className="shopify-related-link">Auth0 SMS MFA</Link>
            <Link to="/integrations/microsoft-teams" className="shopify-related-link">Microsoft Teams SMS</Link>
            <span className="shopify-related-link">Shopify SMS Marketing Australia</span>
            <span className="shopify-related-link">Abandoned Cart SMS Recovery</span>
            <span className="shopify-related-link">Ecommerce SMS Notifications</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="shopify-cta" data-aos="fade-up">
        <div className="shopify-inner">
          <h2>Ready to Add SMS to Your Shopify Store?</h2>
          <p>Install the DataFlows SMS app from the Shopify App Store and start sending in minutes.</p>
          <div className="shopify-cta-buttons">
            <a
              href="https://apps.shopify.com/dataflows-sms"
              target="_blank"
              rel="noreferrer noopener"
              className="shopify-btn-primary"
              onClick={() => trackEvent('shopify_app_store_clicked', { source: 'cta' })}
            >
              Install on Shopify App Store
            </a>
            <a href="/contact-us" className="shopify-btn-outline">Talk to us</a>
          </div>
        </div>
      </section>

      <Footer />

      {showScrollTop && (
        <div className="scroll-to-top show" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <svg viewBox="0 0 24 24" fill="none"><path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/></svg>
        </div>
      )}
    </div>
  )
}

export default ShopifyIntegration
